import type { ObjectRecord, Primitive } from '@vp/shared-types';
import { isSupportedDiagramVersion, layout, parse, sequenceLabels, toPrimitives, transformPrimitives, type Geometry, type GeoNode, type Measure, type ParseResult, type ShapeRegistry } from '@vp/dskhema';
import { canvasMeasure, fontString, textEpoch } from './text';
import { DEFAULT_TEXT_STYLE } from '@vp/core';
import type { ModuleRenderProvider } from './display';

/**
 * `core:diagram` objects (docs/ru/DSKHEMA.md §3): the DSkhema source is parsed and laid out
 * (cached by text), then drawn with regular primitives scaled uniformly into the object box.
 * Module node forms are drawn by their module through the render provider.
 */
export interface DiagramContext {
  shapes?: ShapeRegistry;
  locale?: string;
  modules?: ModuleRenderProvider;
  /** Format version of module object types (module node forms). */
  formatVersion?: (type: string) => number | undefined;
  /** Manifest default props of module object types. */
  moduleDefaults?: (type: string) => Record<string, unknown> | undefined;
  /** Shown on canvas when `props.langVersion` is not current (DSkhema v2). */
  outdatedLabel?: string;
}

/** Text width with the canvas, in the platform's default font. */
export const diagramMeasure: Measure = (text, fontSize, bold) => {
  const k = fontSize / 100;
  return canvasMeasure(text, fontString({ ...DEFAULT_TEXT_STYLE, fontSize: 100, bold }, 100)) * k;
};

interface Laid {
  parsed: ParseResult;
  geometry: Geometry | null;
}

const cache = new Map<string, Laid>();
let cacheEpoch = 0;

/** Parse + layout with a cache keyed by source, locale and the module forms in use. */
export function layoutDiagram(source: string, ctx: Pick<DiagramContext, 'shapes' | 'locale'> = {}, measure: Measure = diagramMeasure): Laid {
  const key = `${ctx.locale ?? ''}\u0001${ctx.shapes ? [...ctx.shapes].join(',') : ''}\u0001${source}`;
  if (cacheEpoch !== textEpoch()) {
    cache.clear();
    cacheEpoch = textEpoch();
  }
  const hit = cache.get(key);
  if (hit) return hit;
  const parsed = parse(source, { shapes: ctx.shapes });
  const geometry = parsed.model ? layout(parsed.model, measure, sequenceLabels(ctx.locale ?? 'en')) : null;
  const laid = { parsed, geometry };
  if (cache.size > 200) cache.clear();
  cache.set(key, laid);
  return laid;
}

/** Natural size of a diagram source (used when creating or editing). */
export function diagramNaturalSize(source: string, ctx: Pick<DiagramContext, 'shapes' | 'locale'> = {}): { w: number; h: number } {
  const g = layoutDiagram(source, ctx).geometry;
  return g ? { w: g.width, h: g.height } : { w: 240, h: 120 };
}

/** Uniform scale and offset that fit the diagram into the object box, centered. */
export function diagramFit(g: Geometry, w: number, h: number): { s: number; dx: number; dy: number } {
  const s = Math.min(w / g.width, h / g.height);
  return { s, dx: (w - g.width * s) / 2, dy: (h - g.height * s) / 2 };
}

function hash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0).toString(36);
}

/** Id of the stand-in object a module draws for a diagram node: `<diagram id>::<hash>`. */
export function diagramNodeObjectId(diagramId: string, nodeId: string): string {
  return `${diagramId}::${hash(nodeId)}`;
}

function moduleNode(obj: ObjectRecord, n: GeoNode, ctx: DiagramContext): Primitive[] | null {
  if (!ctx.modules || typeof n.shape !== 'string' || !n.shape.startsWith('module:')) return null;
  const type = n.shape.slice(7);
  const props = { ...(ctx.moduleDefaults?.(type) ?? {}), label: n.label };
  const stand: ObjectRecord = {
    ...obj,
    id: diagramNodeObjectId(obj.id, n.id),
    type,
    x: 0,
    y: 0,
    w: n.w,
    h: n.h,
    rotation: 0,
    props,
    formatVersion: ctx.formatVersion?.(type) ?? 1,
    parentId: null,
    frameId: null,
  };
  const r = ctx.modules.primitives(stand, Number.parseInt(hash(`${n.w}x${n.h}:${n.label}`), 36));
  if (r.status === 'unavailable' || !r.prims) return null;
  return transformPrimitives(r.prims, 1, n.x, n.y);
}

function diagramPlaceholder(obj: ObjectRecord, label: string): Primitive[] {
  return [
    { k: 'shape', shape: 'rect', x: 0, y: 0, w: obj.w, h: obj.h, radius: 8, fill: { kind: 'solid', color: '#f8fafc' }, stroke: { color: '#cbd5e1', width: 1, dash: 'dashed' } },
    { k: 'text', x: 8, y: 0, w: Math.max(0, obj.w - 16), h: obj.h, text: label, style: { ...DEFAULT_TEXT_STYLE, fontSize: 13, color: '#64748b', align: 'center', valign: 'middle', lineHeight: 1.35 } },
  ];
}

export function diagramPrimitives(obj: ObjectRecord, ctx: DiagramContext): Primitive[] {
  if (!isSupportedDiagramVersion(obj.props['langVersion'])) {
    return diagramPlaceholder(obj, ctx.outdatedLabel ?? 'DSkhema');
  }
  const source = typeof obj.props['source'] === 'string' ? (obj.props['source'] as string) : '';
  const { geometry } = layoutDiagram(source, ctx);
  if (!geometry) {
    return diagramPlaceholder(obj, 'DSkhema');
  }
  // module node forms replace their placeholders when the module has drawn them
  const prims: Primitive[] = [];
  const labels: Primitive[] = [];
  for (const it of geometry.items) {
    const own = it.k === 'node' ? moduleNode(obj, it, ctx) : null;
    const part = own ?? toPrimitives({ ...geometry, items: [it] });
    if (it.k === 'edge') {
      // link labels are drawn above every link and node
      for (const p of part) (p.k === 'path' ? prims : labels).push(p);
    } else prims.push(...part);
  }
  prims.push(...labels);
  const { s, dx, dy } = diagramFit(geometry, obj.w, obj.h);
  return transformPrimitives(prims, s, dx, dy);
}
